import { ArrowLeft, PackageOpen, Copy } from 'lucide-react'
import toast, { Toaster } from 'react-hot-toast'
import { useEffect, useState } from 'react'
import { Button, Card, Col, Container, Row } from 'react-bootstrap'
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams
} from 'react-router-dom'
import api from '../../../config/AxiosInstance'
import {
  capitalizeFirstLetter,
  convertDateWithoutTime,
  transformKey
} from '../../../helpers/constants'
import * as Common from '../../../utils/Common'

const headerTitleStyle = {
  color: 'var(--primary-orange)',
  fontSize: '24px',
  fontWeight: '600'
}

const cardStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  borderRadius: '12px',
  padding: '2rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#959FA9',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: '0.25rem'
}

const valueStyle = {
  fontSize: '1rem',
  fontWeight: '600',
  color: '#333'
}

function MasterStockQuarantineRejectPage() {
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const { state } = useLocation()
  const [data, setData] = useState(null)
  const navigate = useNavigate()

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard!', {
      position: 'top-right',
      style: {
        fontSize: '14px',
        fontWeight: '600'
      }
    })
  }

  const getData = async () => {
    try {
      let url = `master/stocks/karantina/${id}`
      if (searchParams.get('type') === 'reject') {
        url = `master/stocks/reject/${id}`
      }
      const response = await api.get(url)
      const { success, data } = response.data
      if (success) {
        setData(data)
      }
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
      navigate(-1)
    }
  }

  const handlePressBack = () => {
    navigate('/master/stock', {
      state: {
        warehouse_id: state?.warehouse_id,
        item_name: state?.item_name,
        type: state?.type,
        typeWarehouse: state?.typeWarehouse
      }
    })
  }

  useEffect(() => {
    if (id) {
      getData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  return (
    <Container fluid className="px-4">
      <Row className="mb-4 align-items-center">
        <Col className="d-flex align-items-center gap-3">
          <Button
            variant="light"
            className="rounded shadow-sm border p-0 d-flex align-items-center justify-content-center"
            style={{ width: '40px', height: '40px' }}
            onClick={handlePressBack}
          >
            <ArrowLeft className="text-secondary" size={20} />
          </Button>
          <div>
            <h3 style={headerTitleStyle} className="mb-0">
              {capitalizeFirstLetter(searchParams.get('type') || '')} Detail
            </h3>
            <small className="text-muted fw-semibold">
              {data?.item_name || '-'}
            </small>
          </div>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <Card style={cardStyle}>
            <div className="d-flex align-items-center mb-4 gap-2">
              <div
                className="rounded d-flex align-items-center justify-content-center"
                style={{
                  width: '40px',
                  height: '40px',
                  backgroundColor: 'rgba(248, 157, 29, 0.1)'
                }}
              >
                <PackageOpen className="text-primary-orange" size={20} />
              </div>
              <h5 className="fw-bold m-0" style={{ color: '#495057' }}>
                Item Information
              </h5>
            </div>

            <Row className="g-4">
              <Col md={3} sm={6}>
                <div className="p-2">
                  <div style={labelStyle}>Quarantine Date</div>
                  <div style={valueStyle}>
                    {data?.date
                      ? searchParams.get('type') === 'reject'
                        ? convertDateWithoutTime(data?.date)
                        : data?.date
                      : '-'}
                  </div>
                </div>
              </Col>
              <Col md={3} sm={6}>
                <div className="p-2">
                  <div style={labelStyle}>Batch</div>
                  <div className="d-flex align-items-center gap-2">
                    <div style={valueStyle}>{data?.batch || '-'}</div>
                    {data?.batch && (
                      <Copy
                        size={14}
                        className="text-muted cursor-pointer"
                        style={{
                          cursor: 'pointer',
                          opacity: 0.5,
                          transition: 'opacity 0.2s'
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.opacity = 1)
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.opacity = 0.5)
                        }
                        onClick={() => handleCopy(data.batch)}
                        title="Copy Batch"
                      />
                    )}
                  </div>
                </div>
              </Col>
              <Col md={3} sm={6}>
                <div className="p-2">
                  <div style={labelStyle}>Location</div>
                  <div className="d-flex align-items-center gap-2">
                    <div style={valueStyle}>{data?.location || '-'}</div>
                    {data?.location && (
                      <Copy
                        size={14}
                        className="text-muted cursor-pointer"
                        style={{
                          cursor: 'pointer',
                          opacity: 0.5,
                          transition: 'opacity 0.2s'
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.opacity = 1)
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.opacity = 0.5)
                        }
                        onClick={() => handleCopy(data.location)}
                        title="Copy Location"
                      />
                    )}
                  </div>
                </div>
              </Col>
              <Col md={3} sm={6}>
                <div className="p-2">
                  <div style={labelStyle}>Pallet ID</div>
                  <div className="d-flex align-items-center gap-2">
                    <div style={valueStyle}>{data?.palet_id || '-'}</div>
                    {data?.palet_id && (
                      <Copy
                        size={14}
                        className="text-muted cursor-pointer"
                        style={{
                          cursor: 'pointer',
                          opacity: 0.5,
                          transition: 'opacity 0.2s'
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.opacity = 1)
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.opacity = 0.5)
                        }
                        onClick={() => handleCopy(data.palet_id)}
                        title="Copy Pallet ID"
                      />
                    )}
                  </div>
                </div>
              </Col>

              <Col md={12}>
                <hr className="my-2 text-muted opacity-25" />
              </Col>

              <Col md={3} sm={6}>
                <div className="p-2">
                  <div style={labelStyle}>Problem</div>
                  <div style={{ ...valueStyle, color: 'var(--bs-danger)' }}>
                    {data?.item_problem
                      ? transformKey(data?.item_problem)
                      : '-'}
                  </div>
                </div>
              </Col>
              <Col md={3} sm={6}>
                <div className="p-2">
                  <div style={labelStyle}>Related Ticket</div>
                  <div className="d-flex align-items-center gap-2">
                    <div style={{ ...valueStyle, color: '#2F80ED' }}>
                      {data?.item_reference || '-'}
                    </div>
                    {data?.item_reference && (
                      <Copy
                        size={14}
                        className="text-muted cursor-pointer"
                        style={{
                          cursor: 'pointer',
                          opacity: 0.5,
                          transition: 'opacity 0.2s'
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.opacity = 1)
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.opacity = 0.5)
                        }
                        onClick={() => handleCopy(data.item_reference)}
                        title="Copy Reference"
                      />
                    )}
                  </div>
                </div>
              </Col>
              <Col md={6} sm={12}>
                <div className="p-2 bg-light rounded border border-light">
                  <div style={labelStyle}>Remark</div>
                  <div style={{ ...valueStyle, fontWeight: '500' }}>
                    {data?.item_remark || '-'}
                  </div>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Toaster />
    </Container>
  )
}

export default MasterStockQuarantineRejectPage
